// app/frontend/pages/Admin/Videos/Index.tsx
import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Group,
  Table,
  Checkbox,
  Badge,
  ActionIcon,
  Menu,
  TextInput,
  Select,
  Pagination,
  Image,
  Modal,
  Grid,
  Card,
  SegmentedControl,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconSearch,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconEye,
  IconEyeOff,
  IconCopy,
  IconPlayerPlay,
  IconRefresh,
  IconLayoutGrid,
  IconLayoutList,
} from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KText, L2KCaption } from "../../../components/ui/Typography";
import { L2KButton, L2KSelect } from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface Video {
  id: number;
  slug: string;
  title: string;
  thumbnail_url: string;
  duration: string;
  skill_level: string;
  published: boolean;
  categories: { id: number; name: string; slug: string }[];
  category_ids: number[];
  created_at: string;
  updated_at: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Props {
  videos: Video[];
  categories: Category[];
  filters: {
    status: string | null;
    skill_level: string | null;
    category_id: number | null;
    search: string | null;
  };
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  stats: {
    total: number;
    published: number;
    drafts: number;
  };
}

export default function VideosIndex({
  videos,
  categories,
  filters,
  pagination,
  stats,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [search, setSearch] = useState(filters.search || "");
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
  const [previewOpened, { open: openPreview, close: closePreview }] =
    useDisclosure(false);
  const [bulkOpened, { open: openBulk, close: closeBulk }] =
    useDisclosure(false);
  const [syncing, setSyncing] = useState(false);

  // View mode: 'table' or 'cards' - defaults based on screen size
  const isDesktop = useMediaQuery("(max-width: 1024px)");
  const [viewMode, setViewMode] = useState<string>("table");

  // Set initial view mode based on screen size
  useEffect(() => {
    if (isDesktop !== undefined) {
      setViewMode(isDesktop ? "cards" : "table");
    }
  }, [isDesktop]);

  const allSelected = videos.length > 0 && selectedIds.length === videos.length;
  const someSelected =
    selectedIds.length > 0 && selectedIds.length < videos.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(videos.map((v) => v.id));
    }
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleFilter = (key: string, value: string | null) => {
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    router.get(`/admin/videos?${params.toString()}`);
  };

  const handleSearch = () => {
    handleFilter("search", search || null);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", page.toString());
    router.get(`/admin/videos?${params.toString()}`);
  };

  const handlePublish = async (video: Video) => {
    try {
      console.log("[handlePublish] Starting publish for video:", video.id);
      const response = await fetch(`/admin/videos/${video.id}/publish`, {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      });
      console.log("[handlePublish] Response status:", response.status, response.ok);
      if (response.ok) {
        l2kNotify.success("Published", `${video.title} is now published`);
        router.reload();
      } else {
        const data = await response.json().catch(() => ({}));
        console.log("[handlePublish] Error data:", data);
        const errorMessage = data.errors?.join(", ") || "Failed to publish video";
        console.log("[handlePublish] Showing error notification:", errorMessage);
        l2kNotify.error("Cannot Publish", errorMessage);
      }
    } catch (error) {
      console.error("[handlePublish] Exception:", error);
      l2kNotify.error("Error", "Failed to publish video");
    }
  };

  const handleUnpublish = async (video: Video) => {
    try {
      const response = await fetch(`/admin/videos/${video.id}/unpublish`, {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
          "Accept": "application/json",
        },
      });
      if (response.ok) {
        l2kNotify.warning("Unpublished", `${video.title} is now a draft`);
        router.reload();
      } else {
        const data = await response.json().catch(() => ({}));
        const errorMessage = data.errors?.join(", ") || "Failed to unpublish video";
        l2kNotify.error("Cannot Unpublish", errorMessage);
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to unpublish video");
    }
  };

  const handleDuplicate = async (video: Video) => {
    try {
      const response = await fetch(`/admin/videos/${video.id}/duplicate`, {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      if (response.ok) {
        l2kNotify.success("Duplicated", `${video.title} has been duplicated`);
        router.reload();
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to duplicate video");
    }
  };

  const handleDelete = async (video: Video) => {
    if (!confirm(`Are you sure you want to delete "${video.title}"?`)) return;

    try {
      const response = await fetch(`/admin/videos/${video.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      if (response.ok) {
        l2kNotify.success("Deleted", `${video.title} has been deleted`);
        router.reload();
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to delete video");
    }
  };

  const handleBulkAction = async (
    action: string,
    extraParams?: Record<string, any>
  ) => {
    if (selectedIds.length === 0) return;

    try {
      const response = await fetch("/admin/videos/bulk_update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        body: JSON.stringify({
          video_ids: selectedIds,
          bulk_action: action,
          ...extraParams,
        }),
      });

      const data = await response.json();
      if (data.success) {
        l2kNotify.success("Success", data.message);
        setSelectedIds([]);
        closeBulk();
        router.reload();
      } else {
        l2kNotify.error("Error", data.error);
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to perform bulk action");
    }
  };

  const handleSyncVimeo = async () => {
    setSyncing(true);
    try {
      const response = await fetch("/admin/videos/sync_vimeo", {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      const data = await response.json();
      l2kNotify.info("Sync Started", data.message);
    } catch (error) {
      l2kNotify.error("Error", "Failed to start sync");
    } finally {
      setSyncing(false);
    }
  };

  const skillLevelColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case "beginner":
        return "green";
      case "improver":
        return "blue";
      case "intermediate":
        return "orange";
      case "advanced":
        return "red";
      default:
        return "gray";
    }
  };

  // Video Card Component for card view
  const VideoCard = ({ video }: { video: Video }) => (
    <Card
      padding="sm"
      radius="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      {/* Thumbnail with checkbox overlay */}
      <Box style={{ position: "relative" }}>
        <Checkbox
          checked={selectedIds.includes(video.id)}
          onChange={() => toggleOne(video.id)}
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 10,
          }}
          styles={{
            input: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              borderColor: "rgba(255, 255, 255, 0.3)",
              "&:checked": {
                backgroundColor: "#FF9800",
                borderColor: "#FF9800",
              },
            },
          }}
        />
        <Box
          style={{
            position: "relative",
            cursor: "pointer",
            borderRadius: "8px",
            overflow: "hidden",
          }}
          onClick={() => {
            setPreviewVideo(video);
            openPreview();
          }}
        >
          <Image
            src={video.thumbnail_url}
            alt={video.title}
            h={140}
            fit="cover"
          />
          <Box
            style={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "rgba(0, 0, 0, 0.75)",
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            <L2KCaption size="xs">{video.duration}</L2KCaption>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Stack gap="xs" mt="sm">
        <L2KText
          size="sm"
          fw={500}
          lineClamp={2}
          style={{ minHeight: "2.5em" }}
        >
          {video.title}
        </L2KText>

        <Group gap={4} wrap="wrap">
          {video.categories.slice(0, 2).map((cat) => (
            <Badge key={cat.id} size="xs" variant="light" color="gray">
              {cat.name}
            </Badge>
          ))}
          {video.categories.length > 2 && (
            <Badge size="xs" variant="light" color="gray">
              +{video.categories.length - 2}
            </Badge>
          )}
        </Group>

        <Group justify="space-between" align="center">
          <Group gap={6}>
            {video.skill_level ? (
              <Badge size="xs" color={skillLevelColor(video.skill_level)}>
                {video.skill_level}
              </Badge>
            ) : (
              <Badge size="xs" color="red" variant="light">
                No level
              </Badge>
            )}
            <Badge
              size="xs"
              color={video.published ? "green" : "gray"}
              variant={video.published ? "filled" : "light"}
            >
              {video.published ? "Live" : "Draft"}
            </Badge>
          </Group>

          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconDotsVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown
              styles={{
                dropdown: {
                  backgroundColor: "rgba(23, 23, 23, 0.95)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                component={Link}
                href={`/admin/videos/${video.id}/edit`}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPlayerPlay size={14} />}
                onClick={() => {
                  setPreviewVideo(video);
                  openPreview();
                }}
              >
                Preview
              </Menu.Item>
              {video.published ? (
                <Menu.Item
                  leftSection={<IconEyeOff size={14} />}
                  onClick={() => handleUnpublish(video)}
                >
                  Unpublish
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={<IconEye size={14} />}
                  onClick={() => handlePublish(video)}
                >
                  Publish
                </Menu.Item>
              )}
              <Menu.Item
                leftSection={<IconCopy size={14} />}
                onClick={() => handleDuplicate(video)}
              >
                Duplicate
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => handleDelete(video)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Stack>
    </Card>
  );

  return (
    <>
      <SEO title="Videos - Admin - Learntokiz" />
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Box>
            <L2KH2 mb="xs">Videos</L2KH2>
            <L2KText variant="secondary">
              {stats.total} total • {stats.published} published • {stats.drafts}{" "}
              drafts
            </L2KText>
          </Box>
          <Group>
            <L2KButton
              variant="secondary"
              leftSection={<IconRefresh size={16} />}
              loading={syncing}
              onClick={handleSyncVimeo}
            >
              Sync Vimeo
            </L2KButton>
          </Group>
        </Group>

        {/* Filters + View Toggle */}
        <Group justify="space-between" wrap="wrap">
          <Group wrap="wrap">
            <TextInput
              placeholder="Search videos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              leftSection={<IconSearch size={16} />}
              styles={{
                input: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#F0F0F0",
                },
              }}
            />
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => handleFilter("status", value)}
              data={[
                { value: "published", label: "Published" },
                { value: "draft", label: "Draft" },
              ]}
              clearable
              styles={{
                input: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#F0F0F0",
                },
              }}
            />
            <Select
              placeholder="Skill Level"
              value={filters.skill_level}
              onChange={(value) => handleFilter("skill_level", value)}
              data={["beginner", "improver", "intermediate", "advanced"]}
              clearable
              styles={{
                input: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#F0F0F0",
                },
              }}
            />
            <Select
              placeholder="Category"
              value={filters.category_id?.toString()}
              onChange={(value) => handleFilter("category_id", value)}
              data={categories.map((c) => ({
                value: c.id.toString(),
                label: c.name,
              }))}
              clearable
              styles={{
                input: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#F0F0F0",
                },
              }}
            />
          </Group>

          {/* View Toggle */}
          <SegmentedControl
            value={viewMode}
            onChange={setViewMode}
            data={[
              {
                value: "table",
                label: (
                  <Group gap={4}>
                    <IconLayoutList size={16} />
                    <span style={{ display: isDesktop ? "none" : "inline" }}>
                      Table
                    </span>
                  </Group>
                ),
              },
              {
                value: "cards",
                label: (
                  <Group gap={4}>
                    <IconLayoutGrid size={16} />
                    <span style={{ display: isDesktop ? "none" : "inline" }}>
                      Cards
                    </span>
                  </Group>
                ),
              },
            ]}
            styles={{
              root: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 152, 0, 0.15)",
              },
              label: {
                color: "#94a3b8",
                fontWeight: 500,
              },
              indicator: {
                backgroundColor: "rgba(255, 152, 0, 0.2)",
                border: "1px solid rgba(255, 152, 0, 0.4)",
              },
            }}
          />
        </Group>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <Group
            p="sm"
            style={{
              backgroundColor: "rgba(255, 152, 0, 0.1)",
              borderRadius: "8px",
              border: "1px solid rgba(255, 152, 0, 0.3)",
            }}
          >
            <L2KText size="sm">{selectedIds.length} selected</L2KText>
            <L2KButton
              size="xs"
              variant="secondary"
              onClick={() => handleBulkAction("publish")}
            >
              Publish
            </L2KButton>
            <L2KButton
              size="xs"
              variant="secondary"
              onClick={() => handleBulkAction("unpublish")}
            >
              Unpublish
            </L2KButton>
            <L2KButton size="xs" variant="ghost" onClick={openBulk}>
              More Actions...
            </L2KButton>
            <L2KButton
              size="xs"
              variant="ghost"
              onClick={() => setSelectedIds([])}
            >
              Clear
            </L2KButton>
          </Group>
        )}

        {/* Table View */}
        {viewMode === "table" && (
          <Box style={{ overflowX: "auto" }}>
            <Table
              horizontalSpacing="md"
              verticalSpacing="sm"
              styles={{
                table: { backgroundColor: "transparent", minWidth: "800px" },
                th: {
                  color: "#888",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  fontFamily: "'Public Sans', sans-serif",
                },
                td: { borderColor: "rgba(255, 255, 255, 0.05)" },
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 40 }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      styles={{
                        input: {
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          "&:checked": {
                            backgroundColor: "#FF9800",
                            borderColor: "#FF9800",
                          },
                        },
                      }}
                    />
                  </Table.Th>
                  <Table.Th>Video</Table.Th>
                  <Table.Th>Categories</Table.Th>
                  <Table.Th>Level</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Updated</Table.Th>
                  <Table.Th style={{ width: 60 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {videos.map((video) => (
                  <Table.Tr key={video.id}>
                    <Table.Td>
                      <Checkbox
                        checked={selectedIds.includes(video.id)}
                        onChange={() => toggleOne(video.id)}
                        styles={{
                          input: {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderColor: "rgba(255, 255, 255, 0.2)",
                            "&:checked": {
                              backgroundColor: "#FF9800",
                              borderColor: "#FF9800",
                            },
                          },
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Box
                          style={{
                            width: 80,
                            height: 45,
                            borderRadius: "4px",
                            overflow: "hidden",
                            flexShrink: 0,
                            position: "relative",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setPreviewVideo(video);
                            openPreview();
                          }}
                        >
                          <Image
                            src={video.thumbnail_url}
                            alt={video.title}
                            fit="cover"
                            h="100%"
                            w="100%"
                          />
                        </Box>
                        <Box style={{ minWidth: 0 }}>
                          <L2KText size="sm" fw={500} lineClamp={1}>
                            {video.title}
                          </L2KText>
                          <L2KCaption variant="tertiary">
                            {video.duration}
                          </L2KCaption>
                        </Box>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {video.categories.slice(0, 2).map((cat) => (
                          <Badge
                            key={cat.id}
                            size="xs"
                            variant="light"
                            color="gray"
                          >
                            {cat.name}
                          </Badge>
                        ))}
                        {video.categories.length > 2 && (
                          <Badge size="xs" variant="light" color="gray">
                            +{video.categories.length - 2}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {video.skill_level ? (
                        <Badge
                          size="sm"
                          color={skillLevelColor(video.skill_level)}
                        >
                          {video.skill_level}
                        </Badge>
                      ) : (
                        <Badge size="sm" color="red" variant="light">
                          Missing
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={video.published ? "green" : "gray"}
                        variant={video.published ? "filled" : "light"}
                      >
                        {video.published ? "Published" : "Draft"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <L2KCaption variant="tertiary">
                        {video.updated_at}
                      </L2KCaption>
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end" withArrow>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown
                          styles={{
                            dropdown: {
                              backgroundColor: "rgba(23, 23, 23, 0.95)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                            },
                          }}
                        >
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            component={Link}
                            href={`/admin/videos/${video.id}/edit`}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconPlayerPlay size={14} />}
                            onClick={() => {
                              setPreviewVideo(video);
                              openPreview();
                            }}
                          >
                            Preview
                          </Menu.Item>
                          {video.published ? (
                            <Menu.Item
                              leftSection={<IconEyeOff size={14} />}
                              onClick={() => handleUnpublish(video)}
                            >
                              Unpublish
                            </Menu.Item>
                          ) : (
                            <Menu.Item
                              leftSection={<IconEye size={14} />}
                              onClick={() => handlePublish(video)}
                            >
                              Publish
                            </Menu.Item>
                          )}
                          <Menu.Item
                            leftSection={<IconCopy size={14} />}
                            onClick={() => handleDuplicate(video)}
                          >
                            Duplicate
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => handleDelete(video)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {/* Card View */}
        {viewMode === "cards" && (
          <Grid>
            {videos.map((video) => (
              <Grid.Col key={video.id} span={{ base: 12, xs: 6, sm: 4, md: 3 }}>
                <VideoCard video={video} />
              </Grid.Col>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <Group justify="center">
            <Pagination
              total={pagination.total_pages}
              value={pagination.page}
              onChange={handlePageChange}
              color="orange"
              styles={{
                control: {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "#D0D0D0",
                  "&[data-active]": {
                    backgroundColor: "#FF9800",
                    borderColor: "#FF9800",
                    color: "#171717",
                  },
                },
              }}
            />
          </Group>
        )}

        {/* Preview Modal */}
        <Modal
          opened={previewOpened}
          onClose={closePreview}
          title={previewVideo?.title}
          size="xl"
          styles={{
            header: { backgroundColor: "#171717" },
            body: { backgroundColor: "#171717" },
            title: { color: "#F0F0F0", fontFamily: "'Public Sans', sans-serif" },
          }}
        >
          {previewVideo && (
            <Box style={{ aspectRatio: "16/9" }}>
              <iframe
                src={`https://player.vimeo.com/video/${
                  previewVideo.slug?.split("-").pop() || ""
                }`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: "8px",
                }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </Box>
          )}
        </Modal>

        {/* Bulk Actions Modal */}
        <Modal
          opened={bulkOpened}
          onClose={closeBulk}
          title="Bulk Actions"
          styles={{
            header: { backgroundColor: "#171717" },
            body: { backgroundColor: "#171717" },
            title: { color: "#F0F0F0", fontFamily: "'Public Sans', sans-serif" },
          }}
        >
          <Stack gap="md">
            <L2KText variant="secondary">
              Apply action to {selectedIds.length} selected videos:
            </L2KText>
            <L2KButton
              fullWidth
              variant="secondary"
              onClick={() => handleBulkAction("publish")}
            >
              Publish All
            </L2KButton>
            <L2KButton
              fullWidth
              variant="secondary"
              onClick={() => handleBulkAction("unpublish")}
            >
              Unpublish All
            </L2KButton>
            <L2KSelect
              label="Assign Category"
              placeholder="Select category"
              data={categories.map((c) => ({
                value: c.id.toString(),
                label: c.name,
              }))}
              onChange={(value) =>
                value &&
                handleBulkAction("assign_category", {
                  category_id: parseInt(value),
                })
              }
            />
            <L2KSelect
              label="Assign Skill Level"
              placeholder="Select skill level"
              data={["beginner", "improver", "intermediate", "advanced"]}
              onChange={(value) =>
                value &&
                handleBulkAction("assign_skill_level", { skill_level: value })
              }
            />
            <L2KButton
              fullWidth
              variant="outline"
              onClick={() => {
                if (
                  confirm(
                    `Delete ${selectedIds.length} videos? This cannot be undone.`
                  )
                ) {
                  handleBulkAction("delete");
                }
              }}
              style={{ borderColor: "#ef4444", color: "#ef4444" }}
            >
              Delete All
            </L2KButton>
          </Stack>
        </Modal>
      </Stack>
    </>
  );
}

VideosIndex.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
);
